import React from 'react';
import {
  EmptyState,
} from '@backstage/core-components';
import { useApi, identityApiRef } from '@backstage/core-plugin-api';
import { useEntity } from '@backstage/plugin-catalog-react';
import {
  Button,
  Box,
  TextField,
  MenuItem,
  Typography,
  IconButton,
  Tooltip,
  Paper,
  Switch,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Collapse,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import RefreshIcon from '@material-ui/icons/Refresh';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import PlayArrowIcon from '@material-ui/icons/PlayArrow';

/* ─── Types ─── */

interface WebVital {
  name: string;
  label: string;
  value: number | null;
  unit: string;
  good: number;
  needsImprovement: number;
}

interface JsError {
  timestamp: string;
  message: string;
  stackTrace: string;
  browser: string;
  url: string;
}

interface PageView {
  url: string;
  views: number;
  avgDuration: number;
  avgLcp: number | null;
  avgTtfb: number | null;
}

/* ─── Styles ─── */

const useStyles = makeStyles(theme => ({
  toolbar: {
    display: 'flex',
    gap: theme.spacing(1),
    alignItems: 'center',
    flexWrap: 'wrap',
    marginBottom: theme.spacing(2),
  },
  vitalsContainer: {
    display: 'flex',
    gap: theme.spacing(2),
    flexWrap: 'wrap',
    marginBottom: theme.spacing(3),
  },
  vitalCard: {
    flex: '1 1 140px',
    minWidth: 140,
    padding: theme.spacing(2),
    borderRadius: 8,
    textAlign: 'center',
    border: `1px solid ${theme.palette.divider}`,
  },
  vitalValue: {
    fontSize: '1.8rem',
    fontWeight: 700,
    marginBottom: 4,
  },
  vitalLabel: {
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
    textTransform: 'uppercase',
    letterSpacing: 1,
    marginBottom: 4,
  },
  good: { color: '#0cce6b' },
  needsImprovement: { color: '#ffa400' },
  poor: { color: '#ff4e42' },
  noData: { color: theme.palette.text.disabled },
  errorTable: {
    marginBottom: theme.spacing(3),
  },
  stackTrace: {
    backgroundColor: '#1e1e1e',
    color: '#d4d4d4',
    fontFamily: '"JetBrains Mono", "Fira Code", Consolas, monospace',
    fontSize: 11,
    padding: theme.spacing(1.5),
    borderRadius: 4,
    whiteSpace: 'pre-wrap' as const,
    wordBreak: 'break-all' as const,
    maxHeight: 200,
    overflowY: 'auto' as const,
  },
  sessionGrid: {
    display: 'flex',
    gap: theme.spacing(2),
    flexWrap: 'wrap',
    marginBottom: theme.spacing(2),
  },
  sessionCard: {
    flex: '1 1 200px',
    padding: theme.spacing(2),
    borderRadius: 8,
    border: `1px solid ${theme.palette.divider}`,
  },
  statusBar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing(0.5, 1),
    backgroundColor: theme.palette.type === 'dark' ? '#252526' : '#f3f3f3',
    borderRadius: '0 0 4px 4px',
    fontSize: 12,
  },
}));

/* ─── Helpers ─── */

function getVitalRating(vital: WebVital): 'good' | 'needsImprovement' | 'poor' | 'noData' {
  if (vital.value === null) return 'noData';
  if (vital.value <= vital.good) return 'good';
  if (vital.value <= vital.needsImprovement) return 'needsImprovement';
  return 'poor';
}

function getRatingLabel(rating: string): string {
  switch (rating) {
    case 'good': return 'Good';
    case 'needsImprovement': return 'Needs Improvement';
    case 'poor': return 'Poor';
    default: return 'No Data';
  }
}

function formatVitalValue(value: number | null, unit: string): string {
  if (value === null) return '-';
  if (unit === 'ms') return `${Math.round(value)}ms`;
  if (unit === 's') return `${(value / 1000).toFixed(1)}s`;
  return value.toFixed(2);
}

function parseLookback(val: string): number {
  const m = val.match(/^(\d+)(m|h|d)$/);
  if (!m) return 3600;
  const n = parseInt(m[1], 10);
  switch (m[2]) {
    case 'm': return n * 60;
    case 'h': return n * 3600;
    case 'd': return n * 86400;
    default: return 3600;
  }
}

function formatTimestamp(nanoStr: string): string {
  const ms = parseInt(nanoStr, 10) / 1e6;
  const d = new Date(ms);
  return d.toLocaleTimeString('ko-KR', { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

/* ─── Component ─── */

export const RumContent = () => {
  const classes = useStyles();
  const { entity } = useEntity();
  const identityApi = useApi(identityApiRef);

  const ns =
    entity?.metadata?.annotations?.['backstage.io/kubernetes-namespace'] ?? '';
  const entityName = entity?.metadata?.name ?? '';

  /* ─── State ─── */
  const [vitals, setVitals] = React.useState<WebVital[]>([
    { name: 'LCP', label: 'Largest Contentful Paint', value: null, unit: 's', good: 2500, needsImprovement: 4000 },
    { name: 'FID', label: 'First Input Delay', value: null, unit: 'ms', good: 100, needsImprovement: 300 },
    { name: 'FCP', label: 'First Contentful Paint', value: null, unit: 'ms', good: 1800, needsImprovement: 3000 },
    { name: 'CLS', label: 'Cumulative Layout Shift', value: null, unit: '', good: 0.1, needsImprovement: 0.25 },
    { name: 'TTFB', label: 'Time to First Byte', value: null, unit: 'ms', good: 800, needsImprovement: 1800 },
    { name: 'Page Load', label: 'Full Page Load Time', value: null, unit: 'ms', good: 3000, needsImprovement: 6000 },
  ]);
  const [errors, setErrors] = React.useState<JsError[]>([]);
  const [pageViews, setPageViews] = React.useState<PageView[]>([]);
  const [sessionCount, setSessionCount] = React.useState<number | null>(null);
  const [totalEvents, setTotalEvents] = React.useState<number | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [sessionExpired, setSessionExpired] = React.useState(false);
  const [lookback, setLookback] = React.useState('1h');
  const [autoRefresh, setAutoRefresh] = React.useState(false);
  const [expandedRow, setExpandedRow] = React.useState<number | null>(null);

  /* ─── Authenticated fetch ─── */
  const authFetch = React.useCallback(async (url: string) => {
    const { token } = await identityApi.getCredentials();
    if (!token) {
      setSessionExpired(true);
      return undefined;
    }
    setSessionExpired(false);
    const resp = await fetch(url, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (resp.status === 401) {
      setSessionExpired(true);
      return undefined;
    }
    return resp;
  }, [identityApi]);

  /* ─── Fetch RUM Data ─── */
  const fetchRumData = React.useCallback(async () => {
    if (!ns) return;
    setLoading(true);
    setError(null);

    try {
      // Loki API: 나노초 타임스탬프 (Date.now()는 밀리초 → ×1e6)
      const now = Date.now() * 1_000_000;
      const start = now - parseLookback(lookback) * 1_000_000_000;
      const serviceName = entityName;

      // 1. Web Vitals from Loki (Faro logs contain web-vitals measurements)
      const vitalsQuery = `{source="faro"} |= "app_name=${serviceName}" |= "kind=measurement" |= "type=web-vitals"`;
      const vitalsParams = new URLSearchParams({
        query: vitalsQuery,
        start: String(start),
        end: String(now),
        limit: '200',
        direction: 'backward',
      });
      const vitalsUrl = `/api/proxy/loki/loki/api/v1/query_range?${vitalsParams.toString()}`;
      const vitalsResp = await authFetch(vitalsUrl);
      const vitalValues: Record<string, number[]> = { lcp: [], fid: [], cls: [], ttfb: [], inp: [], fcp: [], page_load: [] };
      let vitalsStreams: Array<{ values: Array<[string, string]> }> = [];
      if (vitalsResp && vitalsResp.ok) {
        const vitalsData = await vitalsResp.json();
        vitalsStreams = vitalsData?.data?.result || [];
        const streams = vitalsStreams;
        for (const stream of streams) {
          for (const [, line] of stream.values) {
            // Parse key=value format from Faro log lines
            const getVal = (key: string) => {
              const m = line.match(new RegExp(`(?:^|\\s)${key}=([\\d.]+)`));
              return m ? parseFloat(m[1]) : null;
            };
            const lcp = getVal('lcp');
            const cls = getVal('value_cls');
            const ttfb = getVal('ttfb');
            const inp = getVal('inp');
            const fid = getVal('fid');
            const fcp = getVal('fcp');
            const pageLoad = getVal('page_load');
            if (lcp !== null) vitalValues.lcp.push(lcp);
            if (cls !== null) vitalValues.cls.push(cls);
            if (ttfb !== null) vitalValues.ttfb.push(ttfb);
            if (inp !== null) vitalValues.inp.push(inp);
            if (fid !== null) vitalValues.fid.push(fid);
            if (fcp !== null) vitalValues.fcp.push(fcp);
            if (pageLoad !== null) vitalValues.page_load.push(pageLoad);
          }
        }
      }
      const avg = (arr: number[]) => arr.length > 0 ? arr.reduce((a, b) => a + b, 0) / arr.length : null;
      // Use INP if FID not available (modern browsers use INP)
      const fidOrInp = vitalValues.fid.length > 0 ? avg(vitalValues.fid) : avg(vitalValues.inp);
      setVitals(prev => prev.map(v => {
        switch (v.name) {
          case 'LCP': return { ...v, value: avg(vitalValues.lcp) };
          case 'FID': return { ...v, value: fidOrInp };
          case 'FCP': return { ...v, value: avg(vitalValues.fcp) };
          case 'CLS': return { ...v, value: avg(vitalValues.cls) };
          case 'TTFB': return { ...v, value: avg(vitalValues.ttfb) };
          case 'Page Load': return { ...v, value: avg(vitalValues.page_load) };
          default: return v;
        }
      }));

      // 2. JS Errors from Loki
      const errorQuery = `{source="faro"} |= "app_name=${serviceName}" |= "kind=exception"`;
      const errorParams = new URLSearchParams({
        query: errorQuery,
        start: String(start),
        end: String(now),
        limit: '50',
        direction: 'backward',
      });
      const errorUrl = `/api/proxy/loki/loki/api/v1/query_range?${errorParams.toString()}`;
      const errorResp = await authFetch(errorUrl);
      if (errorResp && errorResp.ok) {
        const errorData = await errorResp.json();
        const streams = errorData?.data?.result || [];
        const parsedErrors: JsError[] = [];
        for (const stream of streams) {
          for (const [ts, line] of stream.values) {
            // Faro logs are key=value format, not JSON
            const getField = (key: string) => {
              // Handle quoted values: key="value with spaces"
              const mq = line.match(new RegExp(`${key}="([^"]*)"`));
              if (mq) return mq[1];
              // Handle unquoted values: key=value
              const mu = line.match(new RegExp(`${key}=([^\\s]+)`));
              return mu ? mu[1] : '';
            };
            parsedErrors.push({
              timestamp: formatTimestamp(ts),
              message: getField('value') || getField('type') || line.substring(0, 200),
              stackTrace: getField('stacktrace'),
              browser: `${getField('browser_name')} ${getField('browser_version')}`.trim(),
              url: getField('page_url'),
            });
          }
        }
        setErrors(parsedErrors);
      }

      // 3. Page Views — navigation 이벤트에서 페이지별 방문수/duration 집계
      const navQuery = `{source="faro"} |= "app_name=${serviceName}" |= "faro.performance.navigation"`;
      const navParams = new URLSearchParams({
        query: navQuery,
        start: String(start),
        end: String(now),
        limit: '500',
        direction: 'backward',
      });
      const navResp = await authFetch(`/api/proxy/loki/loki/api/v1/query_range?${navParams.toString()}`);
      if (navResp && navResp.ok) {
        const navData = await navResp.json();
        const pageMap: Record<string, { views: number; durations: number[]; lcps: number[]; ttfbs: number[] }> = {};
        for (const stream of navData?.data?.result || []) {
          for (const [, line] of stream.values) {
            const pageMatch = line.match(/page_url=(\S+)/);
            const durMatch = line.match(/event_data_duration=(\d+)/);
            const page = pageMatch ? pageMatch[1].replace(/https?:\/\/[^/]+/, '') || '/' : '/';
            if (!pageMap[page]) pageMap[page] = { views: 0, durations: [], lcps: [], ttfbs: [] };
            pageMap[page].views++;
            if (durMatch) pageMap[page].durations.push(parseFloat(durMatch[1]));
          }
        }
        // Web Vitals에서 페이지별 LCP/TTFB 매핑
        for (const stream of vitalsStreams) {
          for (const [, line] of stream.values) {
            const pageMatch = line.match(/page_url=(\S+)/);
            const page = pageMatch ? pageMatch[1].replace(/https?:\/\/[^/]+/, '') || '/' : null;
            if (page && pageMap[page]) {
              const lcpMatch = line.match(/(?:^|\s)lcp=([\d.]+)/);
              const ttfbMatch = line.match(/(?:^|\s)ttfb=([\d.]+)/);
              if (lcpMatch) pageMap[page].lcps.push(parseFloat(lcpMatch[1]));
              if (ttfbMatch) pageMap[page].ttfbs.push(parseFloat(ttfbMatch[1]));
            }
          }
        }
        const avgArr = (arr: number[]) => arr.length > 0 ? arr.reduce((a, b) => a + b, 0) / arr.length : null;
        const pvList: PageView[] = Object.entries(pageMap)
          .map(([url, d]) => ({
            url,
            views: d.views,
            avgDuration: avgArr(d.durations) ?? 0,
            avgLcp: avgArr(d.lcps),
            avgTtfb: avgArr(d.ttfbs),
          }))
          .sort((a, b) => b.views - a.views);
        setPageViews(pvList);
      }

      // 4. API Calls 섹션 제거됨 (2026-04-14)
      // 과거 여러 시도에도 Dev UI에 데이터 표시 실패로 사용자 판단 따라 섹션 제거.
      // 필요 시 Traces 탭에서 workday-be-sys 필터로 동등 정보 조회 가능.

      // 5. Session count from Loki (session_resume = 페이지 로드 시 세션 시작/재개)
      const sessionQuery = `count_over_time({source="faro"} |= "app_name=${serviceName}" |= "event_name=session_resume" [${lookback}])`;
      const sessionParams = new URLSearchParams({
        query: sessionQuery,
        start: String(start),
        end: String(now),
        limit: '1',
      });
      const sessionUrl = `/api/proxy/loki/loki/api/v1/query_range?${sessionParams.toString()}`;
      const sessionResp = await authFetch(sessionUrl);
      if (sessionResp && sessionResp.ok) {
        const sessionData = await sessionResp.json();
        const result = sessionData?.data?.result?.[0];
        if (result && result.values.length > 0) {
          const lastVal = result.values[result.values.length - 1];
          setSessionCount(parseInt(lastVal[1], 10));
        } else {
          setSessionCount(0);
        }
      }

      // 4. Total events count
      const eventsQuery = `count_over_time({source="faro"} |= "app_name=${serviceName}" [${lookback}])`;
      const eventsParams = new URLSearchParams({
        query: eventsQuery,
        start: String(start),
        end: String(now),
        limit: '1',
      });
      const eventsUrl = `/api/proxy/loki/loki/api/v1/query_range?${eventsParams.toString()}`;
      const eventsResp = await authFetch(eventsUrl);
      if (eventsResp && eventsResp.ok) {
        const eventsData = await eventsResp.json();
        const result = eventsData?.data?.result?.[0];
        if (result && result.values.length > 0) {
          const lastVal = result.values[result.values.length - 1];
          setTotalEvents(parseInt(lastVal[1], 10));
        } else {
          setTotalEvents(0);
        }
      }
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [ns, entityName, lookback, authFetch]);

  React.useEffect(() => {
    fetchRumData();
  }, [fetchRumData]);

  // Auto-refresh
  React.useEffect(() => {
    if (!autoRefresh) return undefined;
    const interval = setInterval(fetchRumData, 30000);
    return () => clearInterval(interval);
  }, [autoRefresh, fetchRumData]);

  /* ─── Early returns ─── */
  if (!ns) {
    return (
      <EmptyState
        title="No namespace configured"
        missing="info"
        description="backstage.io/kubernetes-namespace annotation is required to display RUM data."
      />
    );
  }

  if (sessionExpired) {
    return (
      <EmptyState
        title="세션이 만료되었습니다"
        missing="info"
        description="페이지를 새로고침하면 정상 동작합니다."
        action={
          <Button
            variant="contained"
            color="primary"
            startIcon={<RefreshIcon />}
            onClick={() => window.location.reload()}
          >
            새로고침
          </Button>
        }
      />
    );
  }

  return (
    <Box>
      {/* ─── Toolbar ─── */}
      <Box className={classes.toolbar}>
        <TextField
          select
          label="Period"
          value={lookback}
          onChange={e => setLookback(e.target.value)}
          variant="outlined"
          size="small"
          style={{ minWidth: 100 }}
        >
          {['15m', '30m', '1h', '3h', '6h', '12h', '1d'].map(v => (
            <MenuItem key={v} value={v}>{v}</MenuItem>
          ))}
        </TextField>

        <FormControlLabel
          control={
            <Switch
              checked={autoRefresh}
              onChange={e => setAutoRefresh(e.target.checked)}
              size="small"
              color="primary"
            />
          }
          label={<Typography variant="caption">Auto (30s)</Typography>}
        />

        <Tooltip title="Refresh">
          <IconButton size="small" onClick={fetchRumData} disabled={loading}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {/* ─── Error ─── */}
      {error && (
        <Paper style={{ padding: 8, marginBottom: 8, backgroundColor: '#ffebee' }}>
          <Typography color="error" variant="body2">{error}</Typography>
        </Paper>
      )}

      {/* ─── Web Vitals ─── */}
      <Typography variant="h6" gutterBottom>Web Vitals</Typography>
      <Box className={classes.vitalsContainer}>
        {vitals.map(vital => {
          const rating = getVitalRating(vital);
          return (
            <Paper key={vital.name} className={classes.vitalCard} elevation={1}>
              <Typography className={classes.vitalLabel}>{vital.name}</Typography>
              <Typography className={`${classes.vitalValue} ${classes[rating]}`}>
                {formatVitalValue(vital.value, vital.unit)}
              </Typography>
              <Typography variant="caption" className={classes[rating]}>
                {getRatingLabel(rating)}
              </Typography>
              <Typography variant="caption" display="block" style={{ color: '#999', marginTop: 4 }}>
                {vital.label}
              </Typography>
            </Paper>
          );
        })}
      </Box>

      {/* ─── JS Errors ─── */}
      <Typography variant="h6" gutterBottom>
        JS Errors
        {errors.length > 0 && (
          <Typography component="span" variant="body2" style={{ marginLeft: 8, color: '#ff4e42' }}>
            ({errors.length}건)
          </Typography>
        )}
      </Typography>
      <TableContainer component={Paper} className={classes.errorTable}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell style={{ width: 30 }} />
              <TableCell style={{ width: 80 }}>시간</TableCell>
              <TableCell>에러 메시지</TableCell>
              <TableCell style={{ width: 120 }}>브라우저</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {errors.length === 0 && (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  <Typography variant="body2" color="textSecondary">
                    {loading ? 'Loading...' : `최근 ${lookback} 동안 에러 없음`}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {errors.map((err, i) => (
              <React.Fragment key={i}>
                <TableRow
                  hover
                  style={{ cursor: err.stackTrace ? 'pointer' : 'default' }}
                  onClick={() => err.stackTrace && setExpandedRow(expandedRow === i ? null : i)}
                >
                  <TableCell>
                    {err.stackTrace && (
                      <IconButton size="small">
                        {expandedRow === i ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                      </IconButton>
                    )}
                  </TableCell>
                  <TableCell style={{ fontFamily: 'monospace', fontSize: 12 }}>
                    {err.timestamp}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" style={{ color: '#ff4e42' }}>
                      {err.message}
                    </Typography>
                    {err.url && (
                      <Typography variant="caption" color="textSecondary">
                        {err.url}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption">{err.browser}</Typography>
                  </TableCell>
                </TableRow>
                {err.stackTrace && (
                  <TableRow>
                    <TableCell colSpan={4} style={{ padding: 0 }}>
                      <Collapse in={expandedRow === i}>
                        <Box className={classes.stackTrace}>
                          {err.stackTrace}
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                )}
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* ─── Page Views ─── */}
      <Typography variant="h6" gutterBottom>
        Page Views
        {pageViews.length > 0 && (
          <Typography component="span" variant="body2" style={{ marginLeft: 8 }}>
            ({pageViews.reduce((s, p) => s + p.views, 0)}건)
          </Typography>
        )}
      </Typography>
      <TableContainer component={Paper} style={{ marginBottom: 24 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>페이지 경로</TableCell>
              <TableCell align="right">방문수</TableCell>
              <TableCell align="right">Avg Duration</TableCell>
              <TableCell align="right">Avg LCP</TableCell>
              <TableCell align="right">Avg TTFB</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {pageViews.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <Typography variant="body2" color="textSecondary">
                    {loading ? 'Loading...' : `최근 ${lookback} 동안 페이지 방문 없음`}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {pageViews.map((pv, i) => (
              <TableRow key={i} hover>
                <TableCell style={{ fontFamily: 'monospace', fontSize: 12 }}>{pv.url}</TableCell>
                <TableCell align="right" style={{ fontWeight: 600 }}>{pv.views}</TableCell>
                <TableCell align="right">{Math.round(pv.avgDuration)}ms</TableCell>
                <TableCell align="right" style={{ color: pv.avgLcp !== null && pv.avgLcp > 4000 ? '#ff4e42' : pv.avgLcp !== null && pv.avgLcp > 2500 ? '#ffa400' : '#0cce6b' }}>
                  {pv.avgLcp !== null ? `${Math.round(pv.avgLcp)}ms` : '-'}
                </TableCell>
                <TableCell align="right" style={{ color: pv.avgTtfb !== null && pv.avgTtfb > 1800 ? '#ff4e42' : pv.avgTtfb !== null && pv.avgTtfb > 800 ? '#ffa400' : '#0cce6b' }}>
                  {pv.avgTtfb !== null ? `${Math.round(pv.avgTtfb)}ms` : '-'}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* ─── API Calls 섹션 제거 (2026-04-14) ───
          사유: 데이터가 Loki/Tempo 모두에 존재하나 포털 UI에서 지속 0건 표시.
          대안: Traces 탭에서 serviceName 필터로 동등 이상 정보 조회 가능. */}

      {/* ─── Sessions & Events ─── */}
      <Typography variant="h6" gutterBottom>Sessions & Events</Typography>
      <Box className={classes.sessionGrid}>
        <Paper className={classes.sessionCard} elevation={1}>
          <Typography variant="caption" color="textSecondary">Sessions</Typography>
          <Typography variant="h4" style={{ fontWeight: 700 }}>
            {sessionCount !== null ? sessionCount : '-'}
          </Typography>
          <Typography variant="caption" color="textSecondary">최근 {lookback}</Typography>
        </Paper>
        <Paper className={classes.sessionCard} elevation={1}>
          <Typography variant="caption" color="textSecondary">Total Events</Typography>
          <Typography variant="h4" style={{ fontWeight: 700 }}>
            {totalEvents !== null ? totalEvents : '-'}
          </Typography>
          <Typography variant="caption" color="textSecondary">최근 {lookback}</Typography>
        </Paper>
        <Paper className={classes.sessionCard} elevation={1}>
          <Typography variant="caption" color="textSecondary">JS Errors</Typography>
          <Typography variant="h4" style={{ fontWeight: 700, color: errors.length > 0 ? '#ff4e42' : undefined }}>
            {errors.length}
          </Typography>
          <Typography variant="caption" color="textSecondary">최근 {lookback}</Typography>
        </Paper>
      </Box>

      {/* ─── Status Bar ─── */}
      <Box className={classes.statusBar}>
        <Typography variant="caption">
          {totalEvents ?? 0} events | {errors.length} errors | {sessionCount ?? 0} sessions
        </Typography>
        <Box style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {autoRefresh && (
            <Typography variant="caption" style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <PlayArrowIcon style={{ fontSize: 14 }} /> Live
            </Typography>
          )}
          <Typography variant="caption" style={{ color: '#858585' }}>
            {lookback}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};
